import * as React from 'react';
import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { MOUSEDOWN } from '../../utils/eventNames';

interface InputProps {
  timesSubmitted?: Number;
  label?: string;
  ariaLabel?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  value?: string;
  placeholder?: string;
  type?: string;
  id?: string;
  handleCancel?: (event: any) => void;
  helperText?: string;
  handleChange?: (event: any) => void;
  onClick?: () => void;
  characterLimit?: number;
  showPlaceholderWhenInactive?: boolean;
  autoComplete?: string;
}

export interface InputHandle {
  handleInputContainerClick: () => void;
  deactivateInput: (event?: any) => void;
}

const ENTER = 'Enter'
const TAB = 'Tab'
const CLEAR_BUTTON_SRC = 'https://assets.quill.org/images/icons/xs/clear-enabled-black.svg'

export const Input = forwardRef<InputHandle, InputProps>(({
  timesSubmitted,
  label,
  ariaLabel,
  error,
  disabled,
  className = '',
  value,
  placeholder,
  type,
  id,
  handleCancel,
  helperText,
  handleChange,
  onClick,
  characterLimit,
  showPlaceholderWhenInactive,
  autoComplete = 'on',
}, ref) => {
  const [inactive, setInactive] = useState(true);
  const [errorAcknowledged, setErrorAcknowledged] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const idToUse = useMemo(() => id || uuid(), [id]);

  useEffect(() => {
    setErrorAcknowledged(false);
  }, [error, timesSubmitted]);

  const deactivateInput = useCallback((event: any) => {
    if (event?.relatedTarget && !nodeRef.current?.contains(event?.relatedTarget)) {
      setInactive(true);
      setTimeout(() => {
        // performing the blur on Safari causes tab navigation to skip the rest of the inputs on the page
        if (!(navigator.userAgent.search("Safari") >= 0 && navigator.userAgent.search("Chrome") < 0)) {
          inputRef.current?.blur();
        }
      }, 0);
    }
  }, []);

  const handleClick = useCallback((e: MouseEvent) => {
    if (!nodeRef.current || !nodeRef.current.contains(e.target as Node)) {
      deactivateInput(e);
    }
  }, [deactivateInput]);

  useEffect(() => {
    document.addEventListener(MOUSEDOWN, handleClick, false);
    return () => document.removeEventListener(MOUSEDOWN, handleClick, false);
  }, [handleClick]);

  const handleInputContainerClick = useCallback(() => {
    if (onClick) { onClick() }
    if (!disabled) {
      setInactive(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [onClick, disabled]);

  useImperativeHandle(ref, () => ({
    handleInputContainerClick,
    deactivateInput,
  }), [handleInputContainerClick, deactivateInput]);

  const handleKeyDownOnInputContainer = (e: React.KeyboardEvent) => {
    if (e.key !== ENTER) { return }
    handleInputContainerClick();
  };

  const handleClickOnUnacknowledgedError = () => {
    setErrorAcknowledged(true);
    setInactive(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleTab = (event: React.KeyboardEvent) => {
    if (event.key === TAB) {
      deactivateInput(event);
    }
  };

  function renderHelperText() {
    if (helperText) {
      return <span className="helper-text">{helperText}</span>
    }
  }

  function renderErrorText() {
    if (error) {
      return <span className="error-text" role="alert">{error}</span>
    }
  }

  function renderCancelSymbol() {
    if (!inactive && handleCancel && value) {
      return <button aria-label="Cancel input" className="interactive-wrapper cancel focus-on-light" onClick={handleCancel} type="button"><img alt="" src={CLEAR_BUTTON_SRC} /></button>
    }
  }

  function renderCharacterLimit() {
    if (characterLimit) {
      return <div className="character-limit"><span>{value.length}/{characterLimit}</span></div>
    }
  }

  const inactiveOrActive = inactive ? 'inactive' : 'active'
  const hasText = value ? 'has-text' : ''
  const hasCharacterLimit = characterLimit ? 'has-character-limit' : ''
  const helperTextElement = renderHelperText()
  const helperStyle = helperTextElement ? 'with-helper' : ''
  const sharedClassNames = `input-container text-input ${inactiveOrActive} ${hasText} ${className} ${hasCharacterLimit} ${helperStyle}`
  const commonProps = {
    ...(!label && (ariaLabel || placeholder) ? { 'aria-label': ariaLabel || placeholder } : {}),
    id: idToUse,
    ref: inputRef,
    onChange: handleChange,
    value,
    type,
    placeholder: (!inactive || showPlaceholderWhenInactive) && placeholder,
    disabled,
    maxLength: characterLimit ? characterLimit : 10000,
    autoComplete
  }

  if (error) {
    if (errorAcknowledged) {
      return (
        <div
          className={`${sharedClassNames} error`}
          onClick={handleInputContainerClick}
          onKeyDown={handleKeyDownOnInputContainer}
          ref={nodeRef}
          tabIndex={-1}
        >
          <label htmlFor={idToUse}>{label}</label>
          <div className="input-and-cancel-container">
            <input {...commonProps} />
            {renderCancelSymbol()}
          </div>
          {helperTextElement}
          {renderCharacterLimit()}
        </div>
      )
    } else {
      return (
        <div
          className={`${sharedClassNames} error unacknowledged`}
          onClick={handleClickOnUnacknowledgedError}
          ref={nodeRef}
        >
          <label htmlFor={idToUse}>{label}</label>
          <div className="input-and-cancel-container">
            <input {...commonProps} onFocus={handleClickOnUnacknowledgedError} />
            {renderCancelSymbol()}
          </div>
          {renderErrorText()}
        </div>
      )
    }
  } else if (inactive) {
    return (
      <div
        className={sharedClassNames}
        onClick={handleInputContainerClick}
        onKeyDown={handleKeyDownOnInputContainer}
        ref={nodeRef}
        tabIndex={-1}
      >
        <label htmlFor={idToUse}>{label}</label>
        <input {...commonProps} onFocus={handleInputContainerClick} />
        {helperTextElement}
        {renderCharacterLimit()}
      </div>
    )
  } else {
    return (
      <div
        className={sharedClassNames}
        ref={nodeRef}
      >
        <label htmlFor={idToUse}>{label}</label>
        <div className="input-and-cancel-container">
          <input {...commonProps} onKeyDown={handleTab} />
          {renderCancelSymbol()}
        </div>
        {helperTextElement}
        {renderCharacterLimit()}
      </div>
    )
  }
});

Input.displayName = 'Input';
